import {
  AvatarGroup, Button, Card, Checkbox, EmptyState, FacetFilter, FeedbackIcon, InlineMessage, Input, Menu, MenuContent, MenuLabel,
  MenuRadioGroup, MenuRadioItem, MenuTrigger, Meter, NavList, OptionSelect, PageAsideToggle, PageContent, PageHeader, Spinner, Tab, TabList, Tabs,
  WorkOrderIcon,
} from '@olytic/foundation';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUser } from '../auth/AuthProvider';
import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { csvFileName, downloadCsv } from '../objects/exportCsv';
import { useLogTime, useLogTimeContext } from './logTime';
import {
  AllWorkGlyph, BoardGlyph, FilesGlyph, GroupGlyph, ListGlyph, MyWorkGlyph, PlusGlyph, RelatedGlyph, TeamGlyph, TimesheetGlyph,
} from './marks';
import {
  byRank, CONTACT, FACETS, type FacetKey, type Facets, type Filters, filtersActive, GROUPS, type GroupKey, groupMove, isCross, type Lookups,
  matches, NO_FACETS, NONE, optionsFor, PROJECT_STATUS_LABEL, type Scope, hoursText, shownView, todayIso, type View, viewShown,
} from './model';
import { usePhotos } from './usePhotos';
import { WorkBoard } from './WorkBoard';
import { WorkFeedback } from './WorkFeedback';
import { WorkFiles } from './WorkFiles';
import { WorkItemModal } from './WorkItemModal';
import { WorkList } from './WorkList';
import { WorkRelated } from './WorkRelated';
import { WorkTeam } from './WorkTeam';
import { WorkTimesheet } from './WorkTimesheet';
import { workItemsCsv } from './workExport';
import {
  archiveItems, createItem, fetchBootstrap, fetchItems, type NewWorkItem, updateItem, type WorkBootstrap, type WorkItem, type WorkItemPatch,
} from './workApi';

const VIEWS: [View, string, ReactNode][] = [
  ['list', 'List', <ListGlyph key="i" />],
  ['board', 'Board', <BoardGlyph key="i" />],
  ['timesheet', 'Timesheet', <TimesheetGlyph key="i" />],
  ['related', 'Related', <RelatedGlyph key="i" />],
  ['files', 'Files', <FilesGlyph key="i" />],
  ['team', 'Team', <TeamGlyph key="i" />],
  ['feedback', 'Feedback', <FeedbackIcon key="i" width={15} height={15} />],
];

/** The drag type a person carries from the toolbar onto a card or row. */
export const PERSON_DRAG = 'application/x-mothership-person';

const PREFS = 'mothership.work.prefs';
interface Prefs {
  view: View;
  hideDone: boolean;
  showArchived: boolean;
  showSubtasks: boolean;
  group: GroupKey;
  projectFilter: string;
  last: string;
  railCollapsed: boolean;
  closedLanes: string[];
}
const DEFAULT_PREFS: Prefs = {
  view: 'board', hideDone: false, showArchived: false, showSubtasks: false, group: 'none', projectFilter: 'active', last: '',
  railCollapsed: false, closedLanes: [],
};
function loadPrefs(): Prefs {
  try {
    return { ...DEFAULT_PREFS, ...(JSON.parse(window.localStorage.getItem(PREFS) ?? '{}') as Partial<Prefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}
function savePrefs(p: Prefs) {
  try {
    window.localStorage.setItem(PREFS, JSON.stringify(p));
  } catch {
    // Private windows keep the defaults.
  }
}

/**
 * Work, the Delivery app's issue tracker: projects down the left, then a
 * project's (or my, or everyone's) work as a List, a Board or a Timesheet,
 * with the project's Files and Team. A work item opens in a large modal.
 */
export function WorkPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  // The Feedback tab is Admins' only (MOTHER-327); the server refuses its routes to anyone else too.
  const admin = useCurrentUser().role === 'admin';
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const setPrefs = (patch: Partial<Prefs>) => setPrefsState((p) => {
    const next = { ...p, ...patch };
    savePrefs(next);
    return next;
  });

  const [boot, setBoot] = useState<WorkBootstrap | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [items, setItems] = useState<WorkItem[] | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [note, setNote] = useState<{ text: string; tone: 'info' | 'error' } | null>(null);
  const noteTimer = useRef<ReturnType<typeof setTimeout>>();
  const say = useCallback((text: string, tone: 'info' | 'error' = 'info') => {
    setNote({ text, tone });
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(null), tone === 'error' ? 6000 : 3200);
  }, []);

  const [q, setQ] = useState('');
  const [facets, setFacets] = useState<Facets>(NO_FACETS);
  const [projectQuery, setProjectQuery] = useState('');
  const { savedAt } = useLogTime();

  useEffect(() => {
    let live = true;
    fetchBootstrap(authenticatedFetch)
      .then((b) => live && setBoot(b))
      .catch((e: unknown) => live && setBootError(e instanceof Error ? e.message : 'Could not load your projects.'));
    return () => {
      live = false;
    };
  }, [reload]);

  // Where we are: ?project=<id>, or ?scope=mine|all.
  const scope: Scope | null = useMemo(() => {
    if (!boot) return null;
    const s = params.get('scope');
    if (s === 'mine') return { kind: 'mine' };
    if (s === 'all') return { kind: 'all' };
    const p = params.get('project') ?? prefs.last;
    const project = boot.projects.find((x) => x.id === p) ?? boot.projects.find((x) => x.status === 'active') ?? boot.projects[0];
    return project ? { kind: 'project', projectId: project.id } : { kind: 'all' };
  }, [boot, params, prefs.last]);
  const cross = scope ? isCross(scope) : false;
  const requestedView = (params.get('view') as View | null) ?? prefs.view;
  const view: View = shownView(requestedView, { cross, admin });
  const openId = params.get('item');

  const go = useCallback((next: { scope?: Scope; view?: View; item?: string | null }) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next.scope) {
        p.delete('project');
        p.delete('scope');
        if (next.scope.kind === 'project') p.set('project', next.scope.projectId);
        else p.set('scope', next.scope.kind);
      }
      if (next.view) p.set('view', next.view);
      if (next.item !== undefined) {
        if (next.item) p.set('item', next.item);
        else p.delete('item');
      }
      return p;
    }, { replace: next.item === undefined });
  }, [setParams]);

  const scopeKey = scope ? (scope.kind === 'project' ? scope.projectId : scope.kind) : '';
  useEffect(() => {
    if (!scope) return undefined;
    let live = true;
    setItemsError(null);
    fetchItems(authenticatedFetch, scope.kind === 'project' ? { project: scope.projectId } : scope.kind === 'mine' ? { mine: true } : { all: true })
      .then((list) => live && setItems(list))
      .catch((e: unknown) => live && setItemsError(e instanceof Error ? e.message : 'Could not load the work items.'));
    return () => {
      live = false;
    };
  }, [scopeKey, reload, savedAt]);

  // Moving between scopes starts filters fresh; a project scope never groups by project.
  useEffect(() => {
    setFacets(NO_FACETS);
    setQ('');
    if (scope?.kind === 'project') {
      if (prefs.group === 'project') setPrefs({ group: 'none' });
      if (prefs.last !== scope.projectId) setPrefs({ last: scope.projectId });
    }
  }, [scopeKey]);

  const project = scope?.kind === 'project' ? boot?.projects.find((p) => p.id === scope.projectId) ?? null : null;
  useDocumentTitle(project ? `${project.code} · Work` : scope?.kind === 'mine' ? 'My Work' : 'All Work');

  const lookups: Lookups = useMemo(() => ({
    projects: new Map((boot?.projects ?? []).map((p) => [p.id, p])),
    people: new Map((boot?.people ?? []).map((p) => [p.id, p])),
    epics: new Map((items ?? []).filter((i) => i.type === 'epic').map((i) => [i.id, i])),
  }), [boot, items]);
  const order = useMemo(() => byRank(lookups.projects), [lookups.projects]);
  const photos = usePhotos(boot?.people ?? []);
  // Contact names: the ones offered to assign, and the ones already assigned (a team-only person is offered none).
  const contactNames = useMemo(() => {
    const out = new Map((boot?.contacts ?? []).map((c) => [c.id, c.name]));
    for (const i of items ?? []) if (i.assignee_contact) out.set(i.assignee_contact.id, i.assignee_contact.name);
    return out;
  }, [boot, items]);
  // A team member by id, or a contact by "contact:" and id (no photo).
  const avatarOf = useCallback((personId: string | null) => {
    if (!personId) return null;
    if (personId.startsWith(CONTACT)) return { name: contactNames.get(personId.slice(CONTACT.length)) ?? 'A contact' };
    const p = lookups.people.get(personId);
    return { name: p?.name ?? 'Someone', ...(photos.get(personId) ? { src: photos.get(personId)! } : {}) };
  }, [contactNames, lookups.people, photos]);

  // People in the toolbar: the project's team, or everyone on the projects in view.
  const scopePeople = useMemo(() => {
    if (!boot || !scope) return [];
    const projectIds = scope.kind === 'project' ? new Set([scope.projectId]) : new Set(boot.projects.map((p) => p.id));
    const ids = new Set(boot.members.filter((m) => projectIds.has(m.project_id)).map((m) => m.app_user_id));
    return boot.people.filter((p) => ids.has(p.id));
  }, [boot, scope]);

  const filters: Filters = { q, facets, hideDone: prefs.hideDone, showArchived: prefs.showArchived };
  const group: GroupKey = !cross && prefs.group === 'project' ? 'none' : prefs.group;

  useLogTimeContext(useMemo(() => {
    const open = openId ? items?.find((i) => i.id === openId) : null;
    if (open) return { kind: 'item' as const, projectId: open.project_id, itemId: open.type === 'epic' ? '' : open.id, label: `${open.key} ${open.title}` };
    if (project) return { kind: 'project' as const, projectId: project.id, label: project.name };
    if (scope) return { kind: 'cross' as const, label: scope.kind === 'mine' ? 'My Work' : 'All Work' };
    return null;
  }, [openId, items, project, scope]));

  // ── Changes ───────────────────────────────────────────────────────────

  const replaceItem = useCallback((next: WorkItem) => setItems((list) => list?.map((i) => (i.id === next.id ? next : i)) ?? list), []);

  const patchItem = useCallback(async (item: WorkItem, patch: WorkItemPatch, text?: string) => {
    const before = items;
    // Show the change at once; the server's answer replaces it.
    const { position: _position, archived, ...fields } = patch;
    setItems((list) => list?.map((i) => (i.id === item.id ? { ...i, ...fields, ...(archived === undefined ? {} : { archived_at: archived ? new Date().toISOString() : null }) } as WorkItem : i)) ?? list);
    try {
      const saved = await updateItem(authenticatedFetch, item.id, patch);
      replaceItem(saved);
      if ('parent_id' in patch || 'archived' in patch || 'type' in patch || 'status' in patch) setReload((n) => n + 1);
      if (text) say(text);
      return saved;
    } catch (e) {
      setItems(before);
      say(e instanceof Error ? e.message : 'Could not save the change.', 'error');
      return null;
    }
  }, [items, replaceItem, say]);

  const create = useCallback(async (body: Omit<NewWorkItem, 'project_id'> & { project_id?: string }, open = false) => {
    const projectId = body.project_id ?? project?.id ?? prefs.last ?? boot?.projects[0]?.id;
    if (!projectId) return null;
    try {
      const created = await createItem(authenticatedFetch, { ...body, project_id: projectId });
      setItems((list) => (list ? [...list, created] : [created]));
      if (open) go({ item: created.id });
      else say(`Created ${created.key}`);
      return created;
    } catch (e) {
      say(e instanceof Error ? e.message : 'Could not create the work item.', 'error');
      return null;
    }
  }, [boot, go, prefs.last, project, say]);

  const assign = useCallback((item: WorkItem, personId: string) => {
    const name = lookups.people.get(personId)?.name ?? 'them';
    if (item.assignee_id === personId) return say(`${item.key} is already assigned to ${name}`);
    if (!boot?.members.some((m) => m.project_id === item.project_id && m.app_user_id === personId)) {
      return say(`Add ${name} to the ${lookups.projects.get(item.project_id)?.code ?? 'project'} team before assigning work`, 'error');
    }
    void patchItem(item, { assignee_id: personId }, `Assigned ${item.key} to ${name}`);
  }, [boot, lookups, patchItem, say]);

  const swimlaneMove = useCallback((item: WorkItem, from: string, to: string): WorkItemPatch | null | false => {
    if (group === 'none') return null;
    const move = groupMove(item, group, from, to);
    if (!move) return null;
    if ('error' in move) {
      say(move.error, 'error');
      return false;
    }
    return move.patch;
  }, [group, say]);

  const archiveDone = useCallback(async (rows: WorkItem[]) => {
    if (rows.length === 0) return;
    try {
      const { archived } = await archiveItems(authenticatedFetch, rows.map((r) => r.id));
      setReload((n) => n + 1);
      say(`Archived ${archived} done work item${archived === 1 ? '' : 's'}`);
    } catch (e) {
      say(e instanceof Error ? e.message : 'Could not archive them.', 'error');
    }
  }, [say]);

  // ── Layout ────────────────────────────────────────────────────────────

  if (bootError) {
    return <PageContent width="fill" className="px-6 py-5"><InlineMessage tone="error" role="alert">{bootError}</InlineMessage></PageContent>;
  }
  if (!boot || !scope) {
    return <PageContent width="fill"><div className="flex justify-center py-10"><Spinner label="Loading your projects" /></div></PageContent>;
  }

  const visibleProjects = boot.projects.filter((p) =>
    (prefs.projectFilter === 'all' || p.status === prefs.projectFilter || p.id === project?.id)
    && (!projectQuery || `${p.code} ${p.name} ${p.account_name}`.toLowerCase().includes(projectQuery.toLowerCase())));
  const statusGroups = prefs.projectFilter === 'all'
    ? Object.keys(PROJECT_STATUS_LABEL).map((s) => ({ key: s, label: PROJECT_STATUS_LABEL[s]!, items: visibleProjects.filter((p) => p.status === s) }))
    : [{ key: prefs.projectFilter, label: PROJECT_STATUS_LABEL[prefs.projectFilter] ?? 'Projects', items: visibleProjects }];
  const selectedKey = scope.kind === 'project' ? scope.projectId : `@${scope.kind}`;
  const pickScope = (key: string) => go({ scope: key === '@mine' ? { kind: 'mine' } : key === '@all' ? { kind: 'all' } : { kind: 'project', projectId: key }, item: null });

  const rail = (
    <NavList
      aria-label="Projects"
      selectedKey={selectedKey}
      onSelect={pickScope}
      headerAction={<PageAsideToggle />}
      header={
        <div className="flex flex-col gap-2.5">
          <OptionSelect
            aria-label="Show projects"
            options={[
              { value: 'active', label: 'Active projects' }, { value: 'planned', label: 'Planned' }, { value: 'on_hold', label: 'On hold' },
              { value: 'done', label: 'Done' }, { value: 'cancelled', label: 'Cancelled' }, { value: 'all', label: 'All projects' },
            ]}
            value={prefs.projectFilter}
            onValueChange={(projectFilter) => setPrefs({ projectFilter })}
          />
          <Input aria-label="Find a project" placeholder="Find a project" value={projectQuery} onChange={(e) => setProjectQuery(e.target.value)} />
        </div>
      }
      groups={[
        {
          key: 'views',
          label: 'Views',
          items: [
            { key: '@mine', label: 'My Work', icon: <MyWorkGlyph /> },
            { key: '@all', label: 'All Work', icon: <AllWorkGlyph /> },
          ],
        },
        ...statusGroups.filter((g) => g.items.length > 0).map((g) => ({
          key: g.key,
          label: g.label,
          items: g.items.map((p) => ({
            key: p.id,
            code: p.code,
            label: p.name,
            title: p.name,
            ...(p.status === 'active' ? {} : { hint: <span className="text-[var(--warning-fg)]">{PROJECT_STATUS_LABEL[p.status]}</span> }),
          })),
        })),
      ]}
      empty={<span className="text-sm text-[var(--fg-muted)]">No projects match.</span>}
    />
  );

  const all = items ?? [];
  const openCount = all.filter((i) => !i.archived_at && i.type !== 'epic' && i.status !== 'done').length;
  const projectsInView = new Set(all.map((i) => i.project_id)).size;
  const budget = project && project.budget_hours !== null && project.budget_hours > 0 ? project.budget_hours : null;
  const tabs = VIEWS.filter(([k]) => viewShown(k, { cross, admin }));
  const workView = view === 'list' || view === 'board';

  const facetDefs = FACETS
    .filter(([k]) => k !== 'project' || cross)
    .map(([key, label]) => ({
      key,
      label,
      options: optionsFor(key, all.filter((i) => !i.archived_at || prefs.showArchived), lookups).map(([value, text]) => ({
        value,
        label: text,
        ...(key === 'assignee' && value !== NONE ? { leading: <AvatarGroup people={[{ id: value, ...avatarOf(value)! }]} /> } : {}),
        ...(key === 'project' ? { leading: <span className="text-xs text-[var(--fg-muted)]">{lookups.projects.get(value)?.code}</span> } : {}),
      })),
    }));

  return (
    <PageContent
      as="section"
      width="fill"
      aside={rail}
      asideLabel="Projects"
      asideCollapsed={prefs.railCollapsed}
      onAsideCollapsedChange={(railCollapsed) => setPrefs({ railCollapsed })}
    >
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <PageHeader
          className="border-b-0"
          eyebrow={project ? `${project.code} · ${project.account_name}` : 'Across projects'}
          title={project ? project.name : scope.kind === 'mine' ? 'My Work' : 'All Work'}
          subtitle={
            <span className="flex flex-wrap gap-x-3 gap-y-1">
              {project ? (
                <span className={project.status === 'active' ? 'font-emphasis text-[var(--fg)]' : 'font-emphasis text-[var(--warning-fg)]'}>
                  {PROJECT_STATUS_LABEL[project.status]}
                </span>
              ) : null}
              <span>
                <span className="font-emphasis tabular-nums text-[var(--fg)]">{openCount}</span>
                {scope.kind === 'mine' ? ' open work items assigned to you' : scope.kind === 'all' ? ` open work items in ${projectsInView} projects` : ' open work items'}
              </span>
            </span>
          }
          actions={budget && project ? (
            <div className="flex min-w-56 flex-col gap-1.5 text-xs text-[var(--fg-muted)]">
              <div className="flex justify-between gap-3">
                <span className="oly-collabel">Budget</span>
                <span className="tabular-nums"><span className="font-emphasis text-[var(--fg-strong)]">{hoursText(project.logged_hours)}</span> of {hoursText(budget)} h</span>
              </div>
              <Meter label="Budget Used" value={Math.min(1, project.logged_hours / budget)} tone={project.logged_hours > budget ? 'warning' : 'accent'} />
            </div>
          ) : undefined}
        >
          <div className="md:hidden">
            <OptionSelect
              aria-label="Project"
              options={[
                { value: '@mine', label: 'My Work' }, { value: '@all', label: 'All Work' },
                ...boot.projects.filter((p) => p.status === 'active' || p.id === project?.id).map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` })),
              ]}
              value={selectedKey}
              onValueChange={pickScope}
            />
          </div>
        </PageHeader>

        <Tabs value={view} onValueChange={(v) => {
          setPrefs({ view: v as View });
          go({ view: v as View });
        }}>
          <TabList className="px-6">
            {tabs.map(([key, label, icon]) => <Tab key={key} value={key}>{icon}{label}</Tab>)}
          </TabList>
        </Tabs>

        {workView ? (
          <div className="flex flex-wrap items-center gap-2.5 px-6 py-3">
            <Input
              aria-label="Search work items"
              placeholder={view === 'board' ? 'Search board' : 'Search work'}
              className="w-56 max-sm:w-full"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {scope.kind !== 'mine' && scopePeople.length ? (
              <AvatarGroup
                people={scopePeople.map((p) => ({ id: p.id, ...avatarOf(p.id)! }))}
                selectedIds={facets.assignee}
                onToggle={(personId) => setFacets((f) => ({
                  ...f, assignee: f.assignee.includes(personId) ? f.assignee.filter((x) => x !== personId) : [...f.assignee, personId],
                }))}
                dragType={PERSON_DRAG}
                max={8}
                size="sm"
              />
            ) : null}
            <FacetFilter facets={facetDefs} value={facets} onChange={(next) => setFacets({ ...NO_FACETS, ...(next as Partial<Facets>) })} />
            <Menu>
              <MenuTrigger asChild>
                <Button variant={group === 'none' ? 'neutral' : 'outline'}>
                  <GroupGlyph />
                  {group === 'none' ? 'Group' : `Group: ${GROUPS.find(([k]) => k === group)?.[1]}`}
                </Button>
              </MenuTrigger>
              <MenuContent align="start" className="min-w-48">
                <MenuLabel>Group by</MenuLabel>
                <MenuRadioGroup value={group} onValueChange={(g) => setPrefs({ group: g as GroupKey })}>
                  {GROUPS.filter(([k]) => k !== 'project' || cross).map(([k, label]) => <MenuRadioItem key={k} value={k}>{label}</MenuRadioItem>)}
                </MenuRadioGroup>
              </MenuContent>
            </Menu>
            {view === 'list' ? (
              <>
                <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
                  <Checkbox checked={prefs.hideDone} onCheckedChange={(v) => setPrefs({ hideDone: v === true })} />Hide done
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
                  <Checkbox checked={prefs.showArchived} onCheckedChange={(v) => setPrefs({ showArchived: v === true })} />Show archived
                </label>
              </>
            ) : scope.kind !== 'mine' ? (
              <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
                <Checkbox checked={prefs.showSubtasks} onCheckedChange={(v) => setPrefs({ showSubtasks: v === true })} />Show subtasks
              </label>
            ) : null}
            {filtersActive(filters) ? (
              <Button variant="link" onClick={() => {
                setQ('');
                setFacets(NO_FACETS);
              }}>Clear filters</Button>
            ) : null}
            <Button
              variant="neutral"
              className="ml-auto"
              title="Download the work items in view as a CSV file"
              onClick={() => {
                const rows = all.filter((i) => matches(i, filters, { list: view === 'list' })).sort(order);
                if (!rows.length) return say('There are no work items in view to export', 'error');
                const name = project ? project.code : scope.kind === 'mine' ? 'my-work' : 'all-work';
                downloadCsv(csvFileName('work', name, todayIso()), workItemsCsv(rows, (id) => lookups.people.get(id)?.name ?? ''));
                say(`Exported ${rows.length} work item${rows.length === 1 ? '' : 's'}`);
              }}
            >
              Export
            </Button>
            <Button variant="brand" onClick={() => void create({ title: 'New work item' }, true)}>
              <PlusGlyph />Create
            </Button>
          </div>
        ) : null}

        {note ? (
          <div className="px-6 pb-2"><InlineMessage tone={note.tone} role={note.tone === 'error' ? 'alert' : 'status'}>{note.text}</InlineMessage></div>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-6 pb-6">
          {itemsError ? (
            <InlineMessage tone="error" role="alert">{itemsError}</InlineMessage>
          ) : items === null ? (
            <div className="flex justify-center py-10"><Spinner label="Loading work" /></div>
          ) : view === 'list' ? (
            <WorkList
              items={all}
              filters={filters}
              group={group}
              order={order}
              lookups={lookups}
              boot={boot}
              avatarOf={avatarOf}
              openId={openId}
              project={project}
              onOpen={(id) => go({ item: id })}
              onPatch={patchItem}
              onCreate={(title) => void create({ title })}
              onAssign={assign}
            />
          ) : view === 'board' ? (
            <WorkBoard
              items={all}
              filters={filters}
              group={group}
              order={order}
              lookups={lookups}
              avatarOf={avatarOf}
              openId={openId}
              showSubtasks={prefs.showSubtasks || scope.kind === 'mine'}
              canQuickAdd={!cross}
              closedLanes={prefs.closedLanes}
              onLaneToggle={(key) => setPrefs({ closedLanes: prefs.closedLanes.includes(key) ? prefs.closedLanes.filter((k) => k !== key) : [...prefs.closedLanes, key] })}
              onOpen={(id) => go({ item: id })}
              onPatch={patchItem}
              onCreate={(status, title) => void create({ title, status })}
              onAssign={assign}
              onSwimlaneMove={swimlaneMove}
              onArchiveDone={archiveDone}
            />
          ) : view === 'timesheet' ? (
            <WorkTimesheet boot={boot} scope={scope} />
          ) : view === 'related' && project ? (
            <WorkRelated project={project} />
          ) : view === 'files' && project ? (
            <WorkFiles project={project} boot={boot} onOpen={(id) => go({ item: id })} say={say} />
          ) : view === 'feedback' && project ? (
            <WorkFeedback project={project} onOpen={(id) => go({ item: id })} onConverted={() => setReload((n) => n + 1)} say={say} />
          ) : view === 'team' && project ? (
            <WorkTeam project={project} boot={boot} avatarOf={avatarOf} onChanged={() => setReload((n) => n + 1)} say={say} />
          ) : (
            <EmptyState icon={<WorkOrderIcon />} title="Nothing to show" description="Choose a project on the left." />
          )}
        </div>
      </Card>

      {openId ? (
        <WorkItemModal
          key={openId}
          itemId={openId}
          boot={boot}
          avatarOf={avatarOf}
          onOpen={(id) => go({ item: id })}
          onClose={() => go({ item: null })}
          onChanged={(item, structural) => {
            if (item) replaceItem(item);
            if (structural) setReload((n) => n + 1);
          }}
          say={say}
        />
      ) : null}
    </PageContent>
  );
}

export type Patch = (item: WorkItem, patch: WorkItemPatch, text?: string) => Promise<WorkItem | null>;
export type AvatarOf = (personId: string | null) => { name: string; src?: string } | null;
export type SwimlaneMove = (item: WorkItem, from: string, to: string) => WorkItemPatch | null | false;
export const facetLabel = (key: FacetKey) => FACETS.find(([k]) => k === key)?.[1] ?? key;
